import { parse as qsParse, stringify as qsStringify } from "qs";
import { parseURL, resolveURL, stringifyParsedURL, withoutTrailingSlash, withTrailingSlash } from "ufo";
export function isSameOrigin(url) {
    if (typeof document === "undefined") {
        return url.startsWith("/");
    }
    else {
        const resolved = resolveURL(document.location.href, url);
        const parsed = parseURL(resolved);
        return parsed.protocol === document.location.protocol && parsed.host === document.location.host;
    }
}
export function isSamePathname(a, b) {
    const left = parseURL(a).pathname.replace(/\/$/, "").toLocaleLowerCase();
    const right = parseURL(b).pathname.replace(/\/$/, "").toLocaleLowerCase();
    return left === right;
}
export function getLinkHandlerMode(e, href, currentRoute) {
    try {
        // Attempt to get the link element, if there is one
        let eventTarget = null;
        if (e) {
            if (e.target && e.target instanceof HTMLAnchorElement) {
                eventTarget = e.target;
            }
            else if (e.currentTarget && e.currentTarget instanceof HTMLAnchorElement) {
                eventTarget = e.currentTarget;
            }
            // Allow key modifiers to work normally
            if (e.ctrlKey || e.metaKey)
                return { mode: "native", href };
        }
        // If we have a target, but no href, use the target's href
        if (!href && eventTarget)
            href = eventTarget.href;
        if (!href)
            return { mode: "native", href };
        if (href.match(/^(mailto|tel|callto|sms|maps):/)) {
            return { mode: "native", href };
        }
        // Resolve the URL and check if it's the same origin
        const resolved = href.startsWith("http")
            ? href
            : href.startsWith("/")
                ? resolveURL(document.location.origin, href)
                : resolveURL(document.location.href, href);
        const parsed = parseURL(resolved);
        const isSameOrigin = parsed.host?.replace(/:.+/, "") === document.location.host.replace(/:.+/, "");
        // External links should be handled natively
        if (!isSameOrigin)
            return { mode: "native", href };
        // If we're in the admin bundle, links shouldn't do anything
        if (env.admin)
            return { mode: "ignore", href };
        // Allow target=_blank to work normally
        if (eventTarget?.getAttribute("target") === "_blank")
            return { mode: "native", href };
        // If the event was already prevent-defaulted, do nothing
        if (e && e.defaultPrevented)
            return { mode: "ignore", href };
        // If it looks like a file link, handle natively
        if (parsed.pathname.match(/\.[a-z0-9]{2,5}$/i))
            return { mode: "native", href };
        // Starts with /wp-, use native (wp-content, wp-admin etc)
        if (parsed.pathname.startsWith("/wp-"))
            return { mode: "native", href };
        // If pathname+query are identical, but the hash is different, then allow the browser to handle it
        if (currentRoute &&
            isSamePathname(currentRoute.pathname, parsed.pathname) &&
            currentRoute.search === parsed.search) {
            if (currentRoute.hash !== parsed.hash) {
                return { mode: "native", href };
            }
            else {
                return { mode: "ignore", href, isSameUrl: true };
            }
        }
        return { mode: "navigate", href };
    }
    catch (err) {
        console.error(err);
        return { mode: "native", href };
    }
}
export function normalizeRoute(route) {
    const pathname = withoutTrailingSlash(route.pathname);
    const uri = stringifyRouteLink({
        pathname: pathname,
        query: route.query,
        hash: route.hash,
    });
    return {
        ...route,
        pathname,
        uri,
        key: route.key ?? route.view,
    };
}
export function stringifyRouteLink(route) {
    return stringifyParsedURL({
        ...route,
        pathname: withTrailingSlash(route.pathname),
        search: stringifyQuery(route.query),
    });
}
export function parseQuery(query) {
    return qsParse(query.replace(/^\?/, ""), {
        ignoreQueryPrefix: true,
    });
}
export function stringifyQuery(query) {
    return qsStringify(query, {
        encodeValuesOnly: true,
        indices: false,
        skipNulls: true,
        arrayFormat: "brackets",
    });
}
export function getRouteMeta(data) {
    return {
        title: data.meta?.head.find((tag) => tag.tagName === "title")?.inner,
        tags: data.meta?.head ?? [],
        editLink: data.editLink,
    };
}
export function addTrailingSlash(path) {
    if (path?.match(/\./)) {
        return path;
    }
    return withTrailingSlash(path ?? undefined, true);
}
